import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ChevronLeft, Loader2, RotateCcw, Save, X } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { adminApi } from '@/services/api';
import { unwrapList } from '@nepal-ecom/shared-utils';
import { FileImageUploader, FileImageGallery } from '@/components/shared/FileImageUploader';
import { Combobox, type ComboboxOption } from '@/components/shared/Combobox';
import {
  VariantEditor, emptyVariantState, variantsToPayload, type VariantState,
} from '@/components/shared/VariantEditor';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, RichTextEditor } from '@nepal-ecom/ui';

interface CategoryOption {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
}

export function ProductCreate() {
  const navigate = useNavigate();

  const categoriesQuery = useQuery({
    queryKey: ['admin', 'categories-for-product-create'],
    queryFn: () => adminApi.getCategories({ limit: 500 }),
    select: (res) => unwrapList<CategoryOption>(res.data.data),
  });

  // ── Form state ────────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    categoryId: '',
    name: '',
    description: '',
    price: '',
    compareAtPrice: '',
    stock: '0',
    sku: '',
    isMadeInNepal: false,
    thumbnailUrl: '',
    images: [] as string[],
    weight: '',
    lengthCm: '',
    widthCm: '',
    heightCm: '',
    leadTimeDays: '',
    codAvailable: true,
    metaTitle: '',
    metaDescription: '',
  });
  const [tags, setTags] = useState<string[]>([]);
  const [tagDraft, setTagDraft] = useState('');
  const [variants, setVariants] = useState<VariantState>(emptyVariantState());

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  // ── Draft autosave ─────────────────────────────────────────────────────────
  // The Add-Product form can hold a lot of unsaved work. If the session expires
  // mid-edit (or the tab is closed), we don't want to lose it — so we mirror the
  // form to localStorage on every change and offer to restore it on return.
  const DRAFT_KEY = 'admin:product-create:draft';
  const [restoreDone, setRestoreDone] = useState(false);
  const [restoredFromDraft, setRestoredFromDraft] = useState(false);

  // Restore once on mount, before persistence is allowed to run.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const saved = JSON.parse(raw) as {
          form?: Partial<typeof form>; tags?: string[]; variants?: VariantState;
        };
        if (saved.form) setForm((f) => ({ ...f, ...saved.form }));
        if (Array.isArray(saved.tags)) setTags(saved.tags);
        if (saved.variants?.variants) setVariants(saved.variants);
        setRestoredFromDraft(true);
      }
    } catch {
      // Corrupt draft — drop it and start clean.
      localStorage.removeItem(DRAFT_KEY);
    }
    setRestoreDone(true);
  }, []);

  // Persist on change (only after the restore attempt, so we never clobber a
  // saved draft with the initial empty defaults).
  useEffect(() => {
    if (!restoreDone) return;
    const nonEmpty =
      !!form.name.trim() || !!form.description.trim() || !!form.price.trim() ||
      !!form.categoryId || tags.length > 0 || variants.variants.length > 0 ||
      !!form.sku.trim() || !!form.thumbnailUrl || form.images.length > 0;
    if (nonEmpty) {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, tags, variants }));
      } catch { /* quota / serialization — non-fatal */ }
    } else {
      localStorage.removeItem(DRAFT_KEY);
    }
  }, [restoreDone, form, tags, variants]);

  const clearDraft = () => localStorage.removeItem(DRAFT_KEY);

  const discardDraft = () => {
    clearDraft();
    setForm({
      categoryId: '', name: '', description: '', price: '', compareAtPrice: '',
      stock: '0', sku: '', isMadeInNepal: false, thumbnailUrl: '', images: [],
      weight: '', lengthCm: '', widthCm: '', heightCm: '', leadTimeDays: '',
      codAvailable: true, metaTitle: '', metaDescription: '',
    });
    setTags([]);
    setVariants(emptyVariantState());
    setRestoredFromDraft(false);
  };

  // ── Flatten the full category tree into combobox options ──────────────────
  // Recurses through every level so deep subcategories (e.g. Electronics >
  // Computer Accessories > Keyboards) are selectable, not just the first two
  // levels.
  const categoryOptions = useMemo<ComboboxOption[]>(() => {
    const list = categoriesQuery.data ?? [];
    const present = new Set(list.map((c) => c.id));
    const childrenOf = new Map<string | null | undefined, CategoryOption[]>();
    for (const c of list) {
      const key = c.parentId ?? null;
      const bucket = childrenOf.get(key) ?? [];
      bucket.push(c);
      childrenOf.set(key, bucket);
    }

    const opts: ComboboxOption[] = [];
    const seen = new Set<string>();
    const walk = (node: CategoryOption, depth: number, rootName: string, trail: string[]) => {
      if (seen.has(node.id)) return; // guard against accidental cycles
      seen.add(node.id);
      opts.push({
        value: node.id,
        label: node.name,
        group: rootName,
        depth,
        keywords: [node.slug, ...trail],
      });
      for (const child of childrenOf.get(node.id) ?? []) {
        walk(child, depth + 1, rootName, [...trail, node.name]);
      }
    };

    // Roots = real top-level categories PLUS orphans whose parent isn't in the
    // fetched set (e.g. an inactive intermediate category). Without surfacing
    // orphans, a deep category under a hidden ancestor would be unreachable and
    // therefore unselectable — the "deep category missing from dropdown" bug.
    const rootNodes = list.filter((c) => !c.parentId || !present.has(c.parentId));
    for (const root of rootNodes) {
      walk(root, 0, root.name, []);
    }
    return opts;
  }, [categoriesQuery.data]);

  // ── Mutation ─────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (payload: Record<string, unknown> & { categoryId: string }) =>
      adminApi.createProduct(payload as Record<string, unknown> & { vendorId: string; categoryId: string }),
    onSuccess: (res) => {
      const id = res.data?.data?.id;
      clearDraft();
      toast.success('Product created', { description: form.name });
      if (id) navigate(`/products/${id}`);
      else navigate('/products');
    },
    onError: (e: any) =>
      toast.error('Could not create product', {
        description: e?.response?.data?.message ?? String(e),
      }),
  });

  const addTag = () => {
    const t = tagDraft.trim();
    if (!t || tags.includes(t)) return;
    setTags([...tags, t]);
    setTagDraft('');
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.categoryId) return toast.error('Pick a category');
    if (!form.name.trim()) return toast.error('Name is required');

    const priceNum = Number(form.price);
    if (!Number.isFinite(priceNum) || priceNum <= 0) return toast.error('Price must be a positive number');
    const stockNum = Number(form.stock);
    if (!Number.isInteger(stockNum) || stockNum < 0) return toast.error('Stock must be a non-negative integer');

    const payload: Record<string, unknown> & { categoryId: string } = {
      categoryId: form.categoryId,
      name: form.name.trim(),
      price: priceNum,
      stock: stockNum,
      isMadeInNepal: form.isMadeInNepal,
    };

    if (form.description.trim()) payload.description = form.description.trim();
    if (form.sku.trim()) payload.sku = form.sku.trim();
    if (form.thumbnailUrl) payload.thumbnailUrl = form.thumbnailUrl;
    if (form.images.length) payload.images = form.images;
    if (tags.length) payload.tags = tags;
    if (form.metaTitle.trim()) payload.metaTitle = form.metaTitle.trim();
    if (form.metaDescription.trim()) payload.metaDescription = form.metaDescription.trim();

    if (form.compareAtPrice.trim()) {
      const v = Number(form.compareAtPrice);
      if (Number.isFinite(v) && v > 0) payload.compareAtPrice = v;
    }
    if (form.weight.trim()) {
      const v = Number(form.weight);
      if (Number.isFinite(v) && v > 0) payload.weight = v;
    }
    const l = Number(form.lengthCm), w = Number(form.widthCm), h = Number(form.heightCm);
    if (l && w && h) {
      payload.dimensions = { length: l, width: w, height: h, unit: 'cm' };
    }

    const attributes: Record<string, string> = {};
    if (form.codAvailable) attributes.codAvailable = 'true';
    if (form.leadTimeDays.trim()) {
      const v = Number(form.leadTimeDays);
      if (Number.isFinite(v) && v >= 0) attributes.leadTimeDays = String(Math.floor(v));
    }
    if (Object.keys(attributes).length) payload.attributes = attributes;

    const variantPayload = variantsToPayload(variants);
    if (variantPayload) {
      payload.variants = variantPayload;
      payload.stock = variantPayload.reduce((sum, v) => sum + (Number(v.stock) || 0), 0);
    }

    createMutation.mutate(payload);
  };

  return (
    <div className="animate-fade-in space-y-6 max-w-5xl">
      <Link to="/products" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ChevronLeft className="h-4 w-4" /> Catalog
      </Link>

      <PageHeader
        title="Add product"
        description="Create a new product for your storefront. Saves as a draft until you publish from the product detail page."
        actions={
          <Button onClick={submit} disabled={createMutation.isPending}>
            {createMutation.isPending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Save className="h-4 w-4" />}
            Create product
          </Button>
        }
      />

      {restoredFromDraft && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm">
          <span className="flex items-center gap-2 text-amber-900">
            <RotateCcw className="h-4 w-4" />
            Restored an unsaved draft from your last session.
          </span>
          <Button type="button" variant="ghost" size="sm" onClick={discardDraft}>
            Discard draft
          </Button>
        </div>
      )}

      <form onSubmit={submit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          {/* ── Left column ──────────────────────────────────────────── */}
          <div className="space-y-6">
            <Card>
              <CardHeader><CardTitle>Basics</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Field label="Name *">
                  <Input
                    value={form.name}
                    onChange={(e) => set('name', e.target.value)}
                    placeholder="e.g. Pashmina Wool Sweater — Charcoal"
                    required
                  />
                </Field>

                <Field label="Description">
                  <RichTextEditor
                    value={form.description}
                    onChange={(html) => set('description', html)}
                    placeholder="What's the product, who's it for, what makes it special?"
                  />
                </Field>

                <Field label="Tags">
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={tagDraft}
                        onChange={(e) => setTagDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                        placeholder="Add a tag and press Enter (e.g. winter, handmade)"
                        className="max-w-sm"
                      />
                      <Button type="button" variant="outline" size="sm" onClick={addTag} disabled={!tagDraft.trim()}>
                        Add
                      </Button>
                    </div>
                    {tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {tags.map((t) => (
                          <span key={t} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs font-medium px-2 py-0.5 rounded-md">
                            {t}
                            <button
                              type="button"
                              onClick={() => setTags(tags.filter((x) => x !== t))}
                              className="text-slate-400 hover:text-red-500"
                              aria-label={`Remove tag ${t}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Field>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Pricing & inventory</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="Price (NPR) *">
                    <Input
                      type="number" min="1" step="0.01"
                      value={form.price}
                      onChange={(e) => set('price', e.target.value)}
                      required
                    />
                  </Field>
                  <Field label="Compare-at price">
                    <Input
                      type="number" min="0" step="0.01"
                      value={form.compareAtPrice}
                      onChange={(e) => set('compareAtPrice', e.target.value)}
                      placeholder="Strike-through"
                    />
                  </Field>
                  <Field label="Stock *">
                    <Input
                      type="number" min="0" step="1"
                      value={form.stock}
                      onChange={(e) => set('stock', e.target.value)}
                      required
                    />
                  </Field>
                </div>
                <Field label="SKU">
                  <Input
                    value={form.sku}
                    onChange={(e) => set('sku', e.target.value)}
                    placeholder="Auto-generated if blank"
                  />
                </Field>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Variants</CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  For clothing, footwear, electronics, etc. Each combination becomes its own SKU with
                  its own price + stock. Leave empty if this is a single-SKU product.
                </p>
              </CardHeader>
              <CardContent>
                <VariantEditor
                  basePrice={Number(form.price) || 0}
                  baseStock={Number(form.stock) || 0}
                  value={variants}
                  onChange={setVariants}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Shipping</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Field label="Weight (kg)">
                  <Input
                    type="number" min="0" step="0.01"
                    value={form.weight}
                    onChange={(e) => set('weight', e.target.value)}
                    className="max-w-[160px]"
                    placeholder="e.g. 0.5"
                  />
                </Field>
                <div>
                  <p className="block text-xs font-semibold text-slate-600 mb-1.5">Dimensions (cm)</p>
                  <div className="grid grid-cols-3 gap-2 max-w-md">
                    <Input
                      type="number" min="0" step="0.1"
                      value={form.lengthCm}
                      onChange={(e) => set('lengthCm', e.target.value)}
                      placeholder="Length"
                    />
                    <Input
                      type="number" min="0" step="0.1"
                      value={form.widthCm}
                      onChange={(e) => set('widthCm', e.target.value)}
                      placeholder="Width"
                    />
                    <Input
                      type="number" min="0" step="0.1"
                      value={form.heightCm}
                      onChange={(e) => set('heightCm', e.target.value)}
                      placeholder="Height"
                    />
                  </div>
                </div>
                <Field label="Lead time (days)">
                  <Input
                    type="number" min="0" step="1"
                    value={form.leadTimeDays}
                    onChange={(e) => set('leadTimeDays', e.target.value)}
                    className="max-w-[160px]"
                    placeholder="e.g. 2"
                  />
                </Field>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Search engine listing</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Optional. Defaults to product name + description.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <Field label="Meta title">
                  <Input
                    value={form.metaTitle}
                    onChange={(e) => set('metaTitle', e.target.value)}
                    placeholder="Up to 60 characters"
                    maxLength={70}
                  />
                </Field>
                <Field label="Meta description">
                  <textarea
                    value={form.metaDescription}
                    onChange={(e) => set('metaDescription', e.target.value)}
                    rows={2}
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
                    placeholder="Up to 160 characters"
                    maxLength={170}
                  />
                </Field>
              </CardContent>
            </Card>
          </div>

          {/* ── Right column: media + category + flags ─────────────────── */}
          <div className="space-y-6">
            <Card>
              <CardHeader><CardTitle>Category</CardTitle></CardHeader>
              <CardContent>
                <Combobox
                  options={categoryOptions}
                  value={form.categoryId}
                  onChange={(v) => set('categoryId', v)}
                  placeholder="Select a category"
                  searchPlaceholder="Search categories…"
                  emptyText="No category matches"
                  loading={categoriesQuery.isLoading}
                  clearable
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Thumbnail</CardTitle></CardHeader>
              <CardContent>
                <FileImageUploader
                  label="Card / list thumbnail"
                  value={form.thumbnailUrl}
                  onChange={(url) => set('thumbnailUrl', url)}
                  aspect="aspect-square"
                  helperText="Used wherever the product appears as a card."
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Gallery</CardTitle></CardHeader>
              <CardContent>
                <FileImageGallery
                  label="Product photos"
                  values={form.images}
                  onChange={(urls) => set('images', urls)}
                  max={10}
                  helperText="First image is the primary. Drag-and-drop multiple files."
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle>Origin & fulfilment</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isMadeInNepal}
                    onChange={(e) => set('isMadeInNepal', e.target.checked)}
                    className="h-4 w-4 mt-0.5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <p className="text-slate-700 font-medium">Made in Nepal</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">Shows a "Made in Nepal" badge on the storefront.</p>
                  </div>
                </label>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.codAvailable}
                    onChange={(e) => set('codAvailable', e.target.checked)}
                    className="h-4 w-4 mt-0.5 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <p className="text-slate-700 font-medium">Cash on delivery</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">Customers can pay when the item arrives.</p>
                  </div>
                </label>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Sticky bottom actions */}
        <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-slate-50/80 backdrop-blur-sm py-3 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 border-t border-slate-200">
          <Button type="button" variant="outline" onClick={() => navigate('/products')}>
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Save className="h-4 w-4" />}
            Create product
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}
